import React, { useRef, useState } from "react";
import { Camera, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Mobile-first photo capture for product requests.
 *
 * - Up to `max` photos (default 3).
 * - Each photo is resized client-side to a max edge of `MAX_EDGE` px and
 *   re-encoded as JPEG q=0.8 so the final base64 payload comfortably fits
 *   within the backend's per-photo 1.5 MB cap.
 * - Uses a single <input type="file" accept="image/*" capture="environment" />.
 *   On phones this lets the OS choose camera vs. gallery. On desktop it
 *   falls back to a normal file picker.
 *
 * Value/onChange contract:
 *   `value`   : array of `data:image/jpeg;base64,...` strings (length ≤ max)
 *   `onChange`: receives the new array.
 */
const MAX_EDGE = 1280; // px (longer side)
const JPEG_QUALITY = 0.8;

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(fr.error || new Error("file read failed"));
    fr.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image decode failed"));
    img.src = src;
  });
}

async function resizeToJpegDataUrl(file) {
  const dataUrl = await readFileAsDataUrl(file);
  const img = await loadImage(dataUrl);
  let { width, height } = img;
  if (width <= 0 || height <= 0) throw new Error("invalid image");
  const longest = Math.max(width, height);
  if (longest > MAX_EDGE) {
    const scale = MAX_EDGE / longest;
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", JPEG_QUALITY);
}

export function PhotoCaptureField({ value = [], onChange, max = 3, disabled = false, onExtractFromImage = null }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const remaining = Math.max(0, max - (value || []).length);
  const triggerPicker = () => {
    if (disabled || busy || remaining === 0 || !inputRef.current) return;
    inputRef.current.value = ""; // allow re-picking the same file
    inputRef.current.click();
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    const next = [...(value || [])];
    let added = 0;
    let rejected = 0;
    let firstAddedDataUrl = null;
    for (const f of files) {
      if (next.length >= max) { rejected++; continue; }
      if (!f.type.startsWith("image/")) { rejected++; continue; }
      try {
        const out = await resizeToJpegDataUrl(f);
        next.push(out);
        if (firstAddedDataUrl === null) firstAddedDataUrl = out;
        added++;
      } catch {
        rejected++;
      }
    }
    if (added > 0) onChange(next);
    if (rejected > 0 && added === 0) {
      toast.error("Could not read that photo. Try a different one.");
    } else if (rejected > 0) {
      toast.warning(`${rejected} photo${rejected === 1 ? "" : "s"} skipped (limit ${max} total or unsupported file)`);
    }
    // If the parent gave us an OCR hook, run it against the first attached
    // photo. This is what turns "Take photo" into a true smart-capture CTA:
    // attaching a product front automatically fills name + brand fields,
    // and the parent's own warning toasts surface when extraction is
    // partial/empty.
    if (added > 0 && firstAddedDataUrl && typeof onExtractFromImage === "function") {
      try { await onExtractFromImage(firstAddedDataUrl); } catch { /* swallow — toast already shown */ }
    }
    setBusy(false);
  };

  const removeAt = (idx) => {
    if (disabled) return;
    const next = (value || []).slice();
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <div data-testid="photo-capture-field">
      <div className="flex items-center justify-between mb-2">
        <label className="data-sku">Product photos</label>
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink/60" data-testid="photo-capture-count">
          {(value || []).length} / {max}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {(value || []).map((src, idx) => (
          <div
            key={idx}
            className="relative aspect-square border border-border rounded-sm overflow-hidden bg-secondary"
            data-testid={`photo-thumb-${idx}`}
          >
            <img
              src={src}
              alt={`Item ${idx + 1}`}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <button
              type="button"
              onClick={() => removeAt(idx)}
              disabled={disabled}
              className="absolute top-1 right-1 w-7 h-7 rounded-full bg-ink/80 text-white flex items-center justify-center active:scale-95"
              aria-label={`Remove photo ${idx + 1}`}
              data-testid={`photo-remove-${idx}`}
            >
              <X size={14} strokeWidth={2.5} />
            </button>
          </div>
        ))}

        {remaining > 0 && (
          <button
            type="button"
            onClick={triggerPicker}
            disabled={disabled || busy}
            className="aspect-square border-2 border-dashed border-ink/40 rounded-sm flex flex-col items-center justify-center gap-1 text-ink/70 hover:text-ink hover:border-ink active:scale-[0.98] transition-all disabled:opacity-50"
            data-testid="photo-capture-add-button"
            aria-label="Take product photo"
          >
            {busy ? <Loader2 className="animate-spin" size={20} /> : <Camera size={22} strokeWidth={2} />}
            <span className="font-display text-[10px] uppercase tracking-wider leading-tight text-center px-1">
              {busy ? "Processing…" : "Take photo"}
            </span>
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={onFiles}
        data-testid="photo-capture-input"
      />
      <p className="font-mono text-[10px] text-ink/50 mt-2 leading-snug">
        Camera or gallery · auto-resized to 1280 px · up to {max} photos.
        {typeof onExtractFromImage === "function" ? " First photo also auto-fills product name + brand." : ""}
      </p>
    </div>
  );
}

export default PhotoCaptureField;
